import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, initializeSessionMonitor, cleanupSessionMonitor } from '@/lib/supabase';
import { Database } from '@/types/database';
import { generateDeviceFingerprint } from '@/utils/deviceFingerprint';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthState {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
}

interface SignInData {
  email: string;
  password: string;
}

// SignUpData interface removed - signup is admin-only

interface AuthContextType extends AuthState {
  // signUp: (data: SignUpData) => Promise<{ user: User | null; error: Error | null }>; // Commented out - admin use only
  signIn: (data: SignInData) => Promise<{ user: User | null; error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
  onSessionExpiry?: () => void;
  onSessionWarning?: () => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ 
  children, 
  onSessionExpiry,
  onSessionWarning 
}) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    loading: true,
    error: null,
  });

  // Fetch user profile
  const fetchProfile = async (userId: string): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      setState(prev => ({ ...prev, loading: true }));
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const profile = await fetchProfile(session.user.id);
          setState({
            user: session.user,
            profile,
            session,
            loading: false,
            error: null,
          });

          // Initialize session monitor
          initializeSessionMonitor(
            onSessionExpiry || (() => {
              setState(prev => ({ ...prev, user: null, profile: null, session: null }));
            }),
            onSessionWarning
          );
        } else {
          setState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Failed to initialize authentication' 
        }));
      }
    };

    initializeAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const profile = await fetchProfile(session.user.id);
          setState({
            user: session.user,
            profile,
            session,
            loading: false,
            error: null,
          });
        } else if (event === 'SIGNED_OUT') {
          setState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: null,
          });
        } else if (event === 'TOKEN_REFRESHED' && session) {
          setState(prev => ({ ...prev, session }));
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      cleanupSessionMonitor();
    };
  }, [onSessionExpiry, onSessionWarning]);

  // Commenting out signUp function - will be used by admin functions only
  /*
  const signUp = async ({ email, password, fullName, role }: SignUpData) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Validate input
      const validatedProfile = profileSchema.parse({ email, full_name: fullName, role });

      // Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: validatedProfile.full_name,
            role: validatedProfile.role,
          },
        },
      });

      if (authError) throw authError;

      if (authData.user) {
        // Create profile in database
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: authData.user.id,
            email: authData.user.email!,
            full_name: validatedProfile.full_name,
            role: validatedProfile.role,
          });

        if (profileError) {
          // Rollback auth if profile creation fails
          await supabase.auth.signOut();
          throw profileError;
        }

        const profile = await fetchProfile(authData.user.id);
        setState({
          user: authData.user,
          profile,
          session: authData.session,
          loading: false,
          error: null,
        });

        return { user: authData.user, error: null };
      }

      setState(prev => ({ ...prev, loading: false }));
      return { user: null, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { user: null, error: error as Error };
    }
  };
  */

  const signIn = async ({ email, password }: SignInData) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const profile = await fetchProfile(data.user.id);
        setState({
          user: data.user,
          profile,
          session: data.session,
          loading: false,
          error: null,
        });

        // Record device login
        try {
          const deviceInfo = await generateDeviceFingerprint();
          await supabase.rpc('record_device_login', {
            p_user_id: data.user.id,
            p_device_fingerprint: deviceInfo.fingerprint,
            p_device_metadata: deviceInfo.metadata,
            p_ip_address: null,
            p_user_agent: navigator.userAgent
          });
        } catch (deviceError) {
          console.error('Failed to record device login:', deviceError);
          // Don't fail the login if device recording fails
        }

        return { user: data.user, error: null };
      }

      setState(prev => ({ ...prev, loading: false }));
      return { user: null, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { user: null, error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      cleanupSessionMonitor();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setState({
        user: null,
        profile: null,
        session: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign out failed';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      if (!state.user) {
        throw new Error('No authenticated user');
      }

      setState(prev => ({ ...prev, loading: true, error: null }));

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', state.user.id)
        .select()
        .single();

      if (error) throw error;

      setState(prev => ({
        ...prev,
        profile: data,
        loading: false,
      }));

      return { error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { error: error as Error };
    }
  };

  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      setState(prev => ({ ...prev, session }));
    } catch (error) {
      console.error('Session refresh error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Session refresh failed';
      setState(prev => ({ ...prev, error: errorMessage }));
    }
  };

  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    ...state,
    // signUp, // Commented out - admin use only
    signIn,
    signOut,
    updateProfile,
    refreshSession,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};